// SchoolsPanel — super-admin creates/manages schools
// Only visible when logged in as super admin (email verification code)

import { useEffect, useState } from 'react';
import { getSchools, createSchool, updateSchool, suspendSchool, sendPaymentReminder } from '../api/client';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { Card, CardHeader } from '../ui/Card';

const EMPTY = { name: '', code: '', location: '', contact_email: '', password: '' };

export default function SchoolsPanel() {
  const [schools,  setSchools]  = useState([]);
  const [form,     setForm]     = useState(EMPTY);
  const [editing,  setEditing]  = useState(null);
  const [msg,      setMsg]      = useState('');
  const [reminderFor, setReminderFor] = useState(null); // school object, or null
  const [reminderText, setReminderText] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setSchools(await getSchools());
    } catch (err) { setMsg('Failed to load: ' + err.message); }
  }

  function startEdit(s) {
    setEditing(s.id);
    setForm({ name: s.name, code: s.code, location: s.location || '', contact_email: s.contact_email || '', password: '' });
  }

  function cancel() { setEditing(null); setForm(EMPTY); setMsg(''); }

  async function toggleSuspend(s) {
    const next = !s.suspended;
    if (!window.confirm(
      next
        ? `Suspend ${s.name}? Their admin panel login will stop working immediately (driver/guardian/staff apps keep working).`
        : `Reinstate ${s.name}'s admin panel access?`
    )) return;
    try {
      await suspendSchool(s.id, next);
      setMsg(next ? `${s.name} suspended ✅` : `${s.name} reinstated ✅`);
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function submitReminder(e) {
    e.preventDefault();
    try {
      await sendPaymentReminder(reminderFor.id, reminderText.trim());
      setMsg(`Reminder queued for ${reminderFor.name} — it'll pop up on their next admin login ✅`);
      setReminderFor(null); setReminderText('');
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  async function save(e) {
    e.preventDefault();
    try {
      if (editing) {
        await updateSchool(editing, {
          name: form.name, contact_email: form.contact_email, location: form.location,
          password: form.password || undefined,
        });
        setMsg('School updated ✅');
      } else {
        await createSchool(form);
        setMsg('School created ✅');
      }
      cancel(); load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    }
  }

  const inputCls = "border border-line rounded-s px-3 py-2 text-sm bg-paper-raised focus:outline-none focus:ring-2 focus:ring-clearance mt-1";
  const labelCls = "flex flex-col text-xs font-bold text-ink-soft";

  return (
    <div>
      <div className="mb-5">
        <h2 className="font-display font-extrabold text-xl text-ink mb-1">Schools</h2>
        <p className="text-sm text-ink-soft">Create a school here first, then switch to the "School Admins" tab to create a login for it.</p>
      </div>

      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s mb-4 inline-block ${msg.includes('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>
          {msg}
        </p>
      )}

      <Card className="p-5 mb-6 max-w-[460px] flex flex-col gap-3">
        <h3 className="font-display font-bold text-[15px] text-ink m-0">{editing ? 'Edit School' : 'Create School'}</h3>

        <form onSubmit={save} className="flex flex-col gap-3">
          <label className={labelCls}>School Name
            <input required className={inputCls} placeholder="e.g. Greenwood Public School"
              value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </label>

          <label className={labelCls}>School Code
            <input required className={inputCls} placeholder="e.g. GRWOOD" disabled={!!editing}
              value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </label>

          <label className={labelCls}>Location
            <input required className={inputCls} placeholder="e.g. Jaunpur, Uttar Pradesh"
              value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </label>

          <label className={labelCls}>Contact Email
            <input type="email" className={inputCls} placeholder="e.g. admin@school.edu"
              value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
          </label>

          <label className={labelCls}>{editing ? 'New School Password (leave blank to keep)' : 'School Password'}
            <input type="password" required={!editing} className={inputCls} placeholder="Shared school login password"
              value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
          </label>

          <div className="flex gap-2 mt-1">
            <Button variant="clearance" type="submit">{editing ? 'Update' : 'Create'}</Button>
            {editing && <Button variant="secondary" type="button" onClick={cancel}>Cancel</Button>}
          </div>
        </form>
      </Card>

      <Card>
        <CardHeader title={`Schools (${schools.length})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Location</th>
                <th className="px-4 py-2.5">Contact Email</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schools.map(s => (
                <tr key={s.id} className="border-t border-line-soft hover:bg-clearance-tint/40">
                  <td className="px-4 py-3 font-semibold text-ink">{s.name}</td>
                  <td className="px-4 py-3"><code className="bg-paper border border-line rounded px-1.5 py-0.5 text-xs">{s.code}</code></td>
                  <td className="px-4 py-3 text-ink-soft">{s.location || '—'}</td>
                  <td className="px-4 py-3 text-ink-soft">{s.contact_email || '—'}</td>
                  <td className="px-4 py-3">
                    {s.suspended ? <Badge tone="critical">Suspended</Badge> : <Badge tone="success">Active</Badge>}
                    {s.payment_reminder_sent_at && (
                      <div className="text-[11px] text-ink-soft mt-1">
                        Reminder sent {new Date(s.payment_reminder_sent_at).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5 flex-wrap">
                      <Button variant="secondary" className="!px-2.5 !py-1 !text-xs" onClick={() => startEdit(s)}>Edit</Button>
                      <Button variant="clearance" className="!px-2.5 !py-1 !text-xs" onClick={() => { setReminderFor(s); setReminderText(''); }}>
                        Remind
                      </Button>
                      <Button variant={s.suspended ? 'primary' : 'danger'} className="!px-2.5 !py-1 !text-xs"
                        onClick={() => toggleSuspend(s)}>
                        {s.suspended ? 'Reinstate' : 'Suspend'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {schools.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-ink-soft">No schools yet — create one above</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {reminderFor && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-[1000]" onClick={() => setReminderFor(null)}>
          <div className="bg-paper-raised rounded-l shadow-2 p-6 w-[440px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg text-ink mt-0 mb-1">Send Payment Reminder — {reminderFor.name}</h3>
            <p className="text-ink-soft text-sm mb-3">Shows as a one-time popup the next time this school's admin logs into the panel.</p>
            <form onSubmit={submitReminder}>
              <textarea required rows={4} className={`${inputCls} w-full resize-y`}
                placeholder="e.g. Your Zorvexl subscription payment is overdue. Please clear the pending amount to avoid service suspension."
                value={reminderText} onChange={e => setReminderText(e.target.value)} />
              <div className="flex gap-2 mt-3">
                <Button variant="clearance" type="submit">Send</Button>
                <Button variant="secondary" type="button" onClick={() => setReminderFor(null)}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
